// Brand Console DATA -- Competitors (standalone viewer).
//
// Consumes ONLY the existing, real, tenant-scoped GET /v2/competitors/{id}
// route (competitorsAPI.list, already wired in api.js and already used by
// CompanyContextPage.js's own CompetitorsSection for CRUD). This page adds
// no new backend endpoint, no new collection, no new write path -- it is a
// read-only DATA/viewer surface, matching AIRawDataPage.js's own pattern.
//
// Company Context remains the ONE canonical place to add/remove a
// competitor (CompanyContextPage.js's CompetitorsSection) -- this page
// deliberately does not duplicate that CRUD; the empty state and a header
// link both point there instead of re-implementing Add/Remove here.
//
// Real response shape (server.py's v2_get_competitors, verified by direct
// read, not assumed): a plain array of
// { id, tenant_id, client_id, brand_name, domain, competitor_type, active,
//   created_at } -- no updated_at field exists on this document, so none
// is rendered. tenant_id is never rendered anywhere on this page (Section
// "CRITICAL SECURITY CHECK": tenant IDs are never exposed in the UI).

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { StatusBadge } from '@/components/brand/Badges';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { competitorsAPI } from '@/lib/api';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';

function dash(v) {
  return v === null || v === undefined || v === '' ? '—' : v;
}
function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

// Same small local sort helper AIRawDataPage.js already uses -- kept
// duplicated rather than extracted (see final report's Phase 3 note):
// this page's sort keys are flat string/boolean fields, not worth a
// shared abstraction on their own.
function sortRows(rows, key, dir) {
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av === bv) return 0;
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    const cmp = av < bv ? -1 : 1;
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}

function SortHeader({ label, col, sort, onSort, testId }) {
  return (
    <th className="px-3 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => onSort(col)} data-testid={testId}>
      {label}{sort.key === col ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

export default function CompetitorsPage() {
  const navigate = useNavigate();
  // P1.9: client resolution is now the shared useWorkspaceSelection() hook
  // (see SignalsPage.js's own comment for the full rationale) instead of
  // an unswitchable first-client fetch.
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const clientId = workspaceId || null;
  const noClient = !loadingClients && clients.length === 0;
  const [competitors, setCompetitors] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    if (!clientId) return;
    setCompetitors(null);
    setError(null);
    competitorsAPI.list(clientId)
      .then((res) => setCompetitors(res.data || []))
      .catch((e) => setError(e));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    if (!competitors) return [];
    return sortRows(competitors, sort.key, sort.dir);
  }, [competitors, sort]);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  return (
    <DashboardLayout>
      <div data-testid="competitors-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Competitors</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">The canonical competitor set Martian uses across AI visibility checks and Search Everywhere for this workspace.</p>
        </div>

        {!noClient && clients.length > 0 && (
          <div className="mb-4">
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          </div>
        )}

        {noClient && <EmptyState title="ADD A BRAND FIRST" description="Competitors appear once Martian has a workspace." />}

        {!noClient && competitors === null && !error && <LoadingState />}
        {!noClient && error && <ErrorState error={error} onRetry={load} />}

        {!noClient && !error && competitors !== null && competitors.length === 0 && (
          <EmptyState
            testId="competitors-empty"
            title="No competitors yet"
            description="Add competitors in Company Context, or approve the ones Martian suggests from your AI answers there."
            actionLabel="Add competitors in Company Context"
            onAction={() => navigate('/company-context')}
          />
        )}

        {!noClient && !error && competitors !== null && competitors.length > 0 && (
          <>
            <p className="text-[10px] text-[var(--text-tertiary)] mb-1.5">{competitors.length} competitor{competitors.length === 1 ? '' : 's'}</p>
            <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] overflow-x-auto" data-testid="competitors-table">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-[var(--border)] text-left text-[var(--text-tertiary)]">
                    <SortHeader label="Name" col="brand_name" sort={sort} onSort={toggleSort} testId="sort-competitor-name" />
                    <SortHeader label="Domain" col="domain" sort={sort} onSort={toggleSort} testId="sort-competitor-domain" />
                    <SortHeader label="Category" col="competitor_type" sort={sort} onSort={toggleSort} testId="sort-competitor-category" />
                    <th className="px-3 py-2">Status</th>
                    <SortHeader label="Created" col="created_at" sort={sort} onSort={toggleSort} testId="sort-competitor-created" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((c) => (
                    <tr
                      key={c.id} data-testid={`competitor-row-${c.id}`}
                      className="border-b border-[var(--border-light,var(--border))] last:border-b-0 cursor-pointer hover:bg-[var(--surface-sunken,var(--canvas-secondary))] focus-within:bg-[var(--surface-sunken,var(--canvas-secondary))]"
                      onClick={() => setSelected(c)}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') setSelected(c); }}
                    >
                      <td className="px-3 py-2 text-[var(--text-primary)] font-medium">{dash(c.brand_name)}</td>
                      <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{dash(c.domain)}</td>
                      <td className="px-3 py-2 text-[var(--text-secondary)]">{dash(c.competitor_type)}</td>
                      <td className="px-3 py-2">{c.active !== null && c.active !== undefined ? <StatusBadge value={c.active ? 'ACTIVE' : 'INACTIVE'} /> : '—'}</td>
                      <td className="px-3 py-2 text-[var(--text-tertiary)] font-mono whitespace-nowrap">{formatDateTime(c.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── Competitor detail ── */}
        <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto bg-[var(--surface)] border-[var(--border)]" data-testid="competitor-detail">
            {selected && (
              <>
                <SheetHeader>
                  <SheetTitle className="text-[var(--text-primary)] text-[14px]">{dash(selected.brand_name)}</SheetTitle>
                  <SheetDescription className="text-[var(--text-tertiary)] text-[11px]">The real, stored competitor record.</SheetDescription>
                </SheetHeader>
                <div className="mt-3 space-y-3 text-[12px]">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Domain</p><p className="text-[var(--text-primary)] font-mono" data-testid="detail-competitor-domain">{dash(selected.domain)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Category</p><p className="text-[var(--text-primary)]">{dash(selected.competitor_type)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Status</p><p>{selected.active !== null && selected.active !== undefined ? <StatusBadge value={selected.active ? 'ACTIVE' : 'INACTIVE'} /> : '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Created</p><p className="text-[var(--text-primary)]">{formatDateTime(selected.created_at)}</p></div>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Record ID</p>
                    <p className="text-[var(--text-tertiary)] font-mono break-all">{dash(selected.id)}</p>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
}
